import { useEffect, useState, useRef, useCallback } from 'react';
import { runErrorInterceptors } from './errorInterceptors';

/**
 * Read hook (GET). Auto-fires `fetcher` on mount and whenever `deps` change.
 * Cancels any in-flight request on unmount or dep change via AbortController.
 *
 *   fetcher: (signal: AbortSignal) => Promise<T>
 *   deps:    dependency array — same semantics as useEffect
 *   opts:    { onError?: (err) => SUPPRESS | undefined }
 *
 * Return shape:
 *   { data, error, loading }
 *
 * Error flow on rejection:
 *   1. AbortError (unmount / dep change) is filtered — no state change.
 *   2. opts.onError (per-call) runs, then global interceptor chain
 *      (see addErrorInterceptor / SUPPRESS).
 *   3. If any handler returned SUPPRESS, the hook clears loading but does
 *      NOT set `error` — the page is assumed to have handled the failure
 *      (modal, redirect, custom UI).
 *   4. Otherwise `error` is set to the rejection reason and surfaces to UI.
 */
export function useBlustreamApiQuery(fetcher, deps = [], opts = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: true });

  const onErrorRef = useRef(opts.onError);
  useEffect(() => { onErrorRef.current = opts.onError; }, [opts.onError]);

  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));

    Promise.resolve()
      .then(() => fetcher(ctrl.signal))
      .then((data) => {
        if (alive) setState({ data, error: null, loading: false });
      })
      .catch((e) => {
        if (!alive) return;
        if (e && e.name === 'AbortError') return;
        const suppressed = runErrorInterceptors(e, onErrorRef.current);
        if (suppressed) {
          setState({ data: null, error: null, loading: false });
        } else {
          setState({ data: null, error: e, loading: false });
        }
      });

    return () => { alive = false; ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return state;
}

/**
 * Mutation hook (POST / PUT / PATCH / DELETE). Imperative — caller invokes
 * `run` on user action.
 *
 *   mutator: (...args, signal: AbortSignal) => Promise<T>
 *   opts:    { onError?: (err) => SUPPRESS | undefined }
 *
 * Return shape:
 *   [run, { data, error, loading }]
 *
 * Calling `run(...args)`:
 *   - aborts any prior in-flight call (prevents double-submit stale races);
 *   - awaits `mutator(...args, signal)`;
 *   - resolves with the result and sets `{ data, loading: false }`;
 *   - on rejection, runs opts.onError then the global chain; if suppressed,
 *     the thrown error is tagged with `.suppressed = true` and state.error
 *     is NOT set (caller filters via `if (e.suppressed) return;`); otherwise
 *     state.error is set and the error is rethrown so the caller's catch
 *     can fall through to inline UX.
 *
 * On unmount, the latest in-flight call is aborted.
 */
export function useBlustreamApiMutation(mutator, opts = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: false });
  const ctrlRef = useRef(null);
  const aliveRef = useRef(true);

  const onErrorRef = useRef(opts.onError);
  useEffect(() => { onErrorRef.current = opts.onError; }, [opts.onError]);

  const run = useCallback(async (...args) => {
    if (ctrlRef.current) ctrlRef.current.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;

    if (aliveRef.current) setState({ data: null, error: null, loading: true });

    try {
      const data = await mutator(...args, ctrl.signal);
      if (aliveRef.current && ctrlRef.current === ctrl) {
        setState({ data, error: null, loading: false });
      }
      return data;
    } catch (e) {
      if (e && e.name === 'AbortError') {
        throw e;
      }
      if (ctrlRef.current !== ctrl) {
        throw e;
      }
      const suppressed = runErrorInterceptors(e, onErrorRef.current);
      if (aliveRef.current) {
        if (suppressed) {
          setState({ data: null, error: null, loading: false });
        } else {
          setState({ data: null, error: e, loading: false });
        }
      }
      if (suppressed && e && typeof e === 'object') {
        try { e.suppressed = true; } catch (_) { /* frozen error, ignore */ }
      }
      throw e;
    }
  }, [mutator]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (ctrlRef.current) ctrlRef.current.abort();
    };
  }, []);

  return [run, state];
}
